import React from 'react';
import { Card, Typography, Switch, Skeleton, App } from 'antd';
import { RobotOutlined, BellOutlined } from '@ant-design/icons';
import { useResponsive } from '../hooks/useResponsive';
import { useGetBoolSettingQuery, useUpdateSettingMutation } from '../services/settingsApi';

const { Title, Text } = Typography;

const DAILY_SUMMARY_KEY = 'AiResumenDiarioActivo';

/**
 * Configuración > Asistente IA. Home of the tenant-facing AI toggles —
 * today the daily digest; future assistant settings land here too.
 */
const AiSettings: React.FC = () => {
  const { isMobile } = useResponsive();
  const { message } = App.useApp();

  const { data: dailySummarySetting, isLoading } = useGetBoolSettingQuery(DAILY_SUMMARY_KEY);
  const [updateSetting, { isLoading: updating }] = useUpdateSettingMutation();

  const handleToggleDailySummary = async (checked: boolean) => {
    try {
      await updateSetting({ key: DAILY_SUMMARY_KEY, value: checked.toString() }).unwrap();
      message.success(checked
        ? 'Resumen diario activado. Se enviará cada mañana con los datos del día anterior.'
        : 'Resumen diario desactivado. Ya no se generarán resúmenes ni alertas.');
    } catch {
      message.error('No se pudo actualizar la configuración. Intenta de nuevo.');
    }
  };

  return (
    <div style={{ padding: isMobile ? 16 : 24 }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
          <RobotOutlined style={{ color: 'var(--color-primary)' }} />
          Asistente IA
        </Title>
        <Text type="secondary">Configuración del asistente y sus notificaciones</Text>
      </div>

      <Card style={{ borderRadius: isMobile ? 8 : 12, maxWidth: 720 }}>
        {isLoading ? (
          <Skeleton active paragraph={{ rows: 2 }} />
        ) : (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
            <BellOutlined style={{ fontSize: 22, color: 'var(--color-primary)', marginTop: 2 }} />
            <div style={{ flex: 1 }}>
              <Text strong style={{ fontSize: 15 }}>Resumen diario por notificación</Text>
              <div style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Cada mañana, los administradores y gerentes reciben una notificación con el
                  resumen del día anterior: ventas, comparativa con la semana pasada, platillo
                  más vendido y alertas (diferencias de caja, caídas de venta, cancelaciones).
                  El resumen también aparece en el Dashboard.
                </Text>
              </div>
            </div>
            <Switch
              checked={dailySummarySetting?.value ?? false}
              onChange={handleToggleDailySummary}
              loading={updating}
              checkedChildren="Sí"
              unCheckedChildren="No"
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default AiSettings;
